// import { memo } from 'react';

import isSameDay from 'date-fns/isSameDay';
import { isArray } from 'lodash';

import { CustomHeader } from '../CustomCellHeader';
import {
  CustomTextCell,
  DateFieldCell,
  EmailFieldCell,
  MultiSelectFieldCell,
  NumberFieldCell,
  PersonFieldCell,
  PhoneFieldCell,
  SelectFieldCell,
  UrlFieldCell,
} from './CustomCells';

// export const CustomFieldEditor = memo(CustomField);
export const CustomFieldRenderer = (field, isReadOnly = false) => {
  const commonProps = {
    field: field.fieldId,
    headerName: field.label,
    editable: !isReadOnly,
    singleClickEdit: true,
  };

  const suppressEnter = (params) => {
    const KEY_ENTER = 'Enter';
    const event = params.event;
    const key = event.key;
    const suppress = key === KEY_ENTER;
    return suppress;
  };

  const textFilterValueGetter = ({ data }) => {
    if (field.fieldId && data[field.fieldId]?.data) {
      return data[field.fieldId].data;
    }
  };

  switch (field.type) {
    case 'multiselect':
      return {
        valueGetter: ({ data }) => {
          return (
            (isArray(data[field.fieldId]?.data) &&
              data[field.fieldId]?.data?.map((i) => i.label)) ||
            []
          );
        },
        valueFormatter: ({ value }) => value?.join(', ') || '',
        cellEditor: ({ api, data }) =>
          field.fieldId && data[field.fieldId] ? (
            <MultiSelectFieldCell
              task={data}
              fieldId={field.fieldId}
              data={data[field.fieldId]?.data}
              api={api}
            />
          ) : (
            <></>
          ),
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        suppressKeyboardEvent: (params) => {
          return suppressEnter(params);
        },
        headerComponent: CustomHeader,
        floatingFilter: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: ({ value }) => {
            if (!value) return '[Unset]';
            return value;
          },
        },
        ...commonProps,
      };
    case 'select':
      return {
        valueGetter: ({ data }) => {
          return (
            (isArray(data[field.fieldId]?.data) &&
              data[field.fieldId]?.data?.map((i) => i.label)) ||
            []
          );
        },
        valueFormatter: ({ value }) => value?.join(', ') || '',
        cellEditor: ({ api, data }) =>
          field.fieldId && data[field.fieldId] ? (
            <SelectFieldCell
              task={data}
              fieldId={field.fieldId}
              data={data[field.fieldId]?.data}
              api={api}
            />
          ) : (
            <></>
          ),
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        suppressKeyboardEvent: (params) => {
          return suppressEnter(params);
        },
        headerComponent: CustomHeader,
        floatingFilter: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: ({ value }) => {
            if (!value) return '[Unset]';
            return value;
          },
        },
        ...commonProps,
      };
    case 'date':
      return {
        valueFormatter: ({ value }) => {
          return value && Array.isArray(value.data) && value.data.length > 0
            ? value?.data?.map((i) => new Date(i).toLocaleDateString()).join('-')
            : '-';
        },
        cellEditor: ({ value, api, data }) =>
          value ? (
            <DateFieldCell task={data} fieldId={value.fieldId} data={value.data} api={api} />
          ) : (
            <> </>
          ),
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        headerComponent: CustomHeader,
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValues) => {
            const cellValue = cellValues.data || [];
            if (cellValue.length < 1) return null;
            if (cellValue.length > 0) {
              const cellDates = cellValue.map((i) => new Date(i));
              if (cellDates.length === 1) {
                const [cellDate] = cellDates;
                if (isSameDay(cellDate, filterLocalDateAtMidnight)) return 0;
                else if (cellDate > filterLocalDateAtMidnight) return 1;
                else if (cellDate < filterLocalDateAtMidnight) return -1;
              } else if (cellDates.length > 1) {
                const cellDate = cellDates[1];
                if (isSameDay(cellDate, filterLocalDateAtMidnight)) return 0;
                else if (cellDate > filterLocalDateAtMidnight) return 1;
                else if (cellDate < filterLocalDateAtMidnight) return -1;
              }
            }
            return null;
          },
        },
        ...commonProps,
      };
    case 'person':
      return {
        valueGetter: ({ data }) => {
          if (
            field.fieldId &&
            data[field.fieldId] &&
            Array.isArray(data[field.fieldId].data || [])
          ) {
            return (data[field.fieldId].data || [])
              ?.filter((i) => i?.email || i?.firstname)
              .map((i) => i?.firstname || '');
          }
          return [];
        },
        cellEditor: ({ api, data }) =>
          field.fieldId && data[field.fieldId] ? (
            <PersonFieldCell
              task={data}
              fieldId={field.fieldId}
              data={data[field.fieldId].data}
              api={api}
            />
          ) : (
            <></>
          ),
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        headerComponent: CustomHeader,
        floatingFilter: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: ({ value }) => {
            if (!value) return '[Unassigned]';
            return value;
          },
        },
        ...commonProps,
      };
    case 'text':
      return {
        valueFormatter: ({ value }) => value?.data || '-',
        cellEditor: ({ value, api, data }) =>
          value ? (
            <CustomTextCell task={data} fieldId={value.fieldId} data={value.data} api={api} />
          ) : (
            <></>
          ),
        suppressKeyboardEvent: (params) => {
          return suppressEnter(params);
        },
        headerComponent: CustomHeader,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        filterValueGetter: textFilterValueGetter,
        ...commonProps,
      };
    case 'phone':
      return {
        valueFormatter: ({ value }) => value?.data || '-',
        cellEditor: ({ value, api, data }) =>
          value ? (
            <PhoneFieldCell task={data} fieldId={value.fieldId} data={value.data} api={api} />
          ) : (
            <></>
          ),
        suppressKeyboardEvent: (params) => {
          return suppressEnter(params);
        },
        headerComponent: CustomHeader,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        filterValueGetter: textFilterValueGetter,
        ...commonProps,
      };
    case 'email':
      return {
        valueFormatter: ({ value }) => value?.data || '-',
        cellEditor: ({ value, api, data }) =>
          value ? (
            <EmailFieldCell task={data} fieldId={value.fieldId} data={value.data} api={api} />
          ) : (
            <></>
          ),
        suppressKeyboardEvent: (params) => {
          return suppressEnter(params);
        },
        headerComponent: CustomHeader,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        filterValueGetter: textFilterValueGetter,
        ...commonProps,
      };
    case 'number':
      return {
        valueFormatter: ({ value }) => value?.data || '-',
        cellEditor: ({ value, api, data }) =>
          value ? (
            <NumberFieldCell task={data} fieldId={value.fieldId} data={value.data} api={api} />
          ) : (
            <></>
          ),
        suppressKeyboardEvent: (params) => {
          return suppressEnter(params);
        },
        headerComponent: CustomHeader,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        filterValueGetter: textFilterValueGetter,
        ...commonProps,
      };
    case 'url':
      return {
        valueFormatter: ({ value }) => value?.data || '-',
        cellEditor: ({ value, api, data }) =>
          value ? (
            <UrlFieldCell task={data} fieldId={value.fieldId} data={value.data} api={api} />
          ) : (
            <></>
          ),
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        suppressKeyboardEvent: (params) => {
          return suppressEnter(params);
        },
        headerComponent: CustomHeader,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        filterValueGetter: textFilterValueGetter,
        ...commonProps,
      };
    default:
      return {
        ...commonProps,
        field: field.fieldId,
        headerName: field.label,
        editable: false,
        valueFormatter: ({ value }) => '-',
      };
  }
};
